import { alertMessage } from "@redotech/redo-web/alert";
import { memo, useContext, useEffect, useMemo, useState } from "react";
import { useMatches, useNavigate } from "react-router-dom";
import { PermissionsContext } from "../auth/permissions-context";
import { TeamContext } from "../team";
import { UserContext } from "../user";
import { PermissionHandle } from "./protected-routes";

export const RouterProtector = memo(function RouterProtector({
  children,
}: {
  children: React.ReactNode;
}) {
  const team = useContext(TeamContext);
  const user = useContext(UserContext);
  const userPermissions = useContext(PermissionsContext);

  const matches = useMatches();
  const navigate = useNavigate();

  const [mostRecentSuccessfulPath, setMostRecentSuccessfulPath] = useState<
    string | null
  >(null);

  const permissionChecks = useMemo(() => {
    return matches
      .map((m) => m.handle as { permission?: PermissionHandle } | undefined)
      .filter(
        (handle): handle is { permission: PermissionHandle } =>
          !!handle?.permission,
      )
      .map((handle) => handle.permission);
  }, [matches]);

  const params = useMemo(() => {
    return matches.reduce(
      (acc, match) => {
        if (match.params) {
          for (const [key, value] of Object.entries(match.params)) {
            if (value) {
              acc[key] = String(value);
            }
          }
        }
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [matches]);

  const shouldBlock: boolean | undefined = useMemo(() => {
    if (!userPermissions || !team || !user) {
      return undefined;
    }

    return permissionChecks.some((routePermission) => {
      if (typeof routePermission === "string") {
        return !userPermissions.has(routePermission);
      } else if (Array.isArray(routePermission) && routePermission.length > 0) {
        const listOfPermissionsThatAllowRouteAccess = routePermission;
        const userHasAnyValidRoutePermission =
          listOfPermissionsThatAllowRouteAccess.some((p) =>
            userPermissions.has(p),
          );
        return !userHasAnyValidRoutePermission;
      } else if (typeof routePermission === "function") {
        return !routePermission({ user, team, params, userPermissions });
      }
      return false;
    });
  }, [permissionChecks, team, user, userPermissions, params]);

  useEffect(() => {
    if (shouldBlock === false) {
      setMostRecentSuccessfulPath(
        matches[matches.length - 1]?.pathname || null,
      );
    }

    if (shouldBlock) {
      console.error("ROUTER PROTECTOR BLOCKING ACCESS");
      alertMessage("You do not have permission to access this page.");
      if (mostRecentSuccessfulPath) {
        navigate(mostRecentSuccessfulPath);
      } else {
        navigate("/");
      }
    }
  }, [shouldBlock, navigate, mostRecentSuccessfulPath, matches]);

  return <>{children}</>;
});
